<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ButtonV4 from 'next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  payment: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['close', 'refresh', 'confirmed']);
const { t } = useI18n();
const store = useStore();

const copied = ref(false);
const isChecking = ref(false);
const paymentConfirmed = ref(false);
const confirmedPlan = ref(null);
const confirmedAddon = ref(null);
const pollingInterval = ref(null);

const isPix = computed(() => props.payment?.type === 'pix');
const isBoleto = computed(() => props.payment?.type === 'boleto');
const isRecurring = computed(() => props.payment?.isRecurring === true);
const cycle = computed(() => props.payment?.cycle || 'Mensal');

const formattedValue = computed(() => {
  const value = props.payment?.value || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
});

const formattedDueDate = computed(() => {
  if (!props.payment?.dueDate) return '';
  const date = new Date(props.payment.dueDate);
  return date.toLocaleDateString('pt-BR');
});

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text);
    copied.value = true;
    useAlert(t('BILLING_SETTINGS.PAYMENT_PENDING.COPY_SUCCESS'));
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    useAlert(t('BILLING_SETTINGS.PAYMENT_PENDING.COPY_ERROR'));
  }
};

const openBoleto = () => {
  if (props.payment?.boletoUrl) {
    window.open(props.payment.boletoUrl, '_blank');
  }
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

const checkPaymentStatus = async () => {
  if (isChecking.value || paymentConfirmed.value) return;

  isChecking.value = true;
  try {
    const result = await store.dispatch('billing/checkPaymentStatus');

    if (result.confirmed) {
      paymentConfirmed.value = true;
      confirmedPlan.value = result.plan;
      confirmedAddon.value = result.addon;
      stopPolling();
      emit('confirmed', result);
    }
  } finally {
    isChecking.value = false;
  }
};

const startPolling = () => {
  // Wait 3 seconds before first check to allow metadata to be saved
  setTimeout(() => {
    checkPaymentStatus();

    // Then check every 5 seconds
    pollingInterval.value = setInterval(() => {
      checkPaymentStatus();
    }, 5000);
  }, 3000);
};

const handleClose = () => {
  if (paymentConfirmed.value) {
    emit('refresh');
  }
  emit('close');
};

onMounted(() => {
  if (isPix.value || isBoleto.value) {
    startPolling();
  }
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Payment Confirmed Success State -->
    <template v-if="paymentConfirmed">
      <div class="text-center py-8">
        <div
          class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-n-green-100 dark:bg-n-green-900/30 mb-6 animate-bounce-once"
        >
          <span class="i-lucide-check-circle text-5xl text-n-green-600" />
        </div>

        <h3 class="text-2xl font-bold text-n-slate-12 mb-2">
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.PAYMENT_CONFIRMED') }}
        </h3>

        <p class="text-lg text-n-slate-10 mb-4">
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.PAYMENT_SUCCESS_DESC') }}
        </p>

        <div
          v-if="confirmedPlan"
          class="inline-flex items-center gap-2 px-4 py-2 bg-n-green-50 dark:bg-n-green-900/20 rounded-full mb-6"
        >
          <span class="i-lucide-crown text-n-green-600" />
          <span
            class="text-sm font-medium text-n-green-700 dark:text-n-green-400"
          >
            {{
              t('BILLING_SETTINGS.PAYMENT_PENDING.PLAN_ACTIVATED', {
                plan: confirmedPlan.name,
              })
            }}
          </span>
        </div>

        <div
          v-else-if="confirmedAddon"
          class="inline-flex items-center gap-2 px-4 py-2 bg-n-green-50 dark:bg-n-green-900/20 rounded-full mb-6"
        >
          <span class="i-lucide-puzzle text-n-green-600" />
          <span
            class="text-sm font-medium text-n-green-700 dark:text-n-green-400"
          >
            {{
              t('BILLING_SETTINGS.PAYMENT_PENDING.ADDON_ACTIVATED', {
                addon: confirmedAddon.name,
                quantity: confirmedAddon.quantity,
              })
            }}
          </span>
        </div>

        <div class="flex flex-col gap-3">
          <ButtonV4 class="w-full" @click="handleClose">
            <span class="i-lucide-check mr-2" />
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.CONTINUE') }}
          </ButtonV4>
        </div>
      </div>
    </template>

    <!-- Waiting for Payment State -->
    <template v-else>
      <!-- Recurring Badge -->
      <div
        v-if="isRecurring"
        class="flex items-center justify-center gap-2 p-2 rounded-lg bg-n-blue-2 border border-n-blue-6"
      >
        <span class="i-lucide-repeat text-n-blue-11" />
        <span class="text-sm font-medium text-n-blue-11">
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.RECURRING_BADGE') }}
          {{ cycle }}
        </span>
      </div>

      <!-- PIX Payment -->
      <template v-if="isPix">
        <div class="text-center">
          <div
            class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-n-teal-100 dark:bg-n-teal-900/30 mb-4"
          >
            <span class="i-lucide-qr-code text-3xl text-n-teal-600" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.PIX_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.PIX_DESC') }}
          </p>
        </div>

        <!-- QR Code -->
        <div class="flex justify-center">
          <div class="p-4 bg-white rounded-xl shadow-sm">
            <img
              v-if="payment.qrCode"
              :src="`data:image/png;base64,${payment.qrCode}`"
              alt="QR Code PIX"
              class="w-48 h-48"
            />
          </div>
        </div>

        <!-- Value -->
        <div class="text-center">
          <div class="text-sm text-n-slate-10">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.VALUE') }}
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ formattedValue }}
          </div>
          <div v-if="isRecurring" class="text-xs text-n-slate-10 mt-1">
            {{
              t('BILLING_SETTINGS.PAYMENT_PENDING.AUTO_CHARGE', {
                cycle: cycle.toLowerCase(),
              })
            }}
          </div>
        </div>

        <!-- Copy PIX Code -->
        <div
          class="flex items-center gap-2 p-3 bg-n-slate-2 rounded-lg cursor-pointer hover:bg-n-slate-3 transition-colors"
          @click="copyToClipboard(payment.copyPaste)"
        >
          <span class="i-lucide-copy text-n-slate-11" />
          <span class="flex-1 text-sm text-n-slate-11 truncate">
            {{ payment.copyPaste }}
          </span>
          <span v-if="copied" class="text-xs text-n-teal-600 font-medium">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.COPIED') }}
          </span>
        </div>

        <div class="text-center text-sm text-n-slate-10">
          <span class="i-lucide-clock mr-1" />
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.PIX_EXPIRES') }}
        </div>
      </template>

      <!-- Boleto Payment -->
      <template v-if="isBoleto">
        <div class="text-center">
          <div
            class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-n-amber-100 dark:bg-n-amber-900/30 mb-4"
          >
            <span class="i-lucide-file-text text-3xl text-n-amber-600" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.BOLETO_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.BOLETO_DESC') }}
          </p>
        </div>

        <!-- Value and Due Date -->
        <div class="grid grid-cols-2 gap-4">
          <div class="p-4 bg-n-slate-2 rounded-lg text-center">
            <div class="text-sm text-n-slate-10">
              {{ t('BILLING_SETTINGS.PAYMENT_PENDING.VALUE_LABEL') }}
            </div>
            <div class="text-xl font-bold text-n-slate-12">
              {{ formattedValue }}
            </div>
            <div v-if="isRecurring" class="text-xs text-n-slate-10 mt-1">
              {{ cycle }}
            </div>
          </div>
          <div class="p-4 bg-n-slate-2 rounded-lg text-center">
            <div class="text-sm text-n-slate-10">
              {{ t('BILLING_SETTINGS.PAYMENT_PENDING.DUE_DATE') }}
            </div>
            <div class="text-xl font-bold text-n-slate-12">
              {{ formattedDueDate }}
            </div>
          </div>
        </div>

        <!-- Barcode -->
        <div v-if="payment.barcode">
          <div class="text-sm text-n-slate-10 mb-2">
            {{ t('BILLING_SETTINGS.PAYMENT_PENDING.BARCODE') }}
          </div>
          <div
            class="flex items-center gap-2 p-3 bg-n-slate-2 rounded-lg cursor-pointer hover:bg-n-slate-3 transition-colors"
            @click="copyToClipboard(payment.barcode)"
          >
            <span class="i-lucide-copy text-n-slate-11" />
            <span class="flex-1 text-xs text-n-slate-11 font-mono break-all">
              {{ payment.barcode }}
            </span>
          </div>
        </div>

        <!-- Open Boleto -->
        <ButtonV4 class="w-full" @click="openBoleto">
          <span class="i-lucide-external-link mr-2" />
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.OPEN_BOLETO') }}
        </ButtonV4>
      </template>

      <!-- Polling Status Indicator -->
      <div
        class="flex items-center justify-center gap-2 p-3 bg-n-blue-50 dark:bg-n-blue-900/20 rounded-lg"
      >
        <span
          class="i-lucide-loader-2 text-n-blue-600 animate-spin"
          :class="{ 'opacity-50': !isChecking }"
        />
        <span class="text-sm text-n-blue-700 dark:text-n-blue-400">
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.WAITING_PAYMENT') }}
        </span>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 pt-4 border-t border-n-slate-3">
        <ButtonV4 class="flex-1" faded slate @click="emit('close')">
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.CLOSE') }}
        </ButtonV4>
        <ButtonV4
          class="flex-1"
          faded
          :disabled="isChecking"
          @click="checkPaymentStatus"
        >
          <span
            class="mr-2"
            :class="
              isChecking
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-refresh-cw'
            "
          />
          {{ t('BILLING_SETTINGS.PAYMENT_PENDING.VERIFY_PAYMENT') }}
        </ButtonV4>
      </div>

      <p class="text-xs text-center text-n-slate-10">
        {{ t('BILLING_SETTINGS.PAYMENT_PENDING.AUTO_ACTIVATE') }}
      </p>
    </template>
  </div>
</template>

<style scoped>
@keyframes bounce-once {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-bounce-once {
  animation: bounce-once 0.5s ease-in-out;
}
</style>
